/**
 * Panneau de revue déplié sous un jalon (maquette écran 2) : une ligne par
 * fiche de validation — nom · statut · lien QMS · validé par — puis le pied
 * de revue (valider / déroger / replanifier).
 *
 * Le hub ne stocke jamais de fichier : la colonne « Document » est une URL vers
 * le QMS SharePoint, validée par le même `isHttpUrl` que les liens de projet.
 */
import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNewOutlined';
import AddIcon from '@mui/icons-material/Add';
import { Timestamp } from 'firebase/firestore';
import { FV_STATUSES, type FvDeliverable, type FvStatus, type Milestone } from '@altyor-kronos/shared-types';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../core/auth/useAuth';
import {
  decideMilestone,
  reopenMilestone,
  rescheduleMilestone,
  updateMilestoneDeliverables,
  updateMilestoneTargetDate,
} from '../../core/data/projectMutations';
import { colors } from '../../core/theme/theme';
import { milestoneCompliance, FV_DONE_STATUSES } from './compliance';
import { isHttpUrl } from './validators';

/**
 * Libellés du PAD, repris à l'identique — ce sont des valeurs de données (comme
 * les codes projet), pas du texte mis en majuscules par la CSS. Ne pas traduire :
 * c'est le vocabulaire que les PM lisent dans le classeur.
 */
const FV_STATUS_LABELS: Record<FvStatus, string> = {
  pass: 'PASS',
  derogated: 'DEROGATED',
  'not-pass': 'NOT PASS',
  validated: 'VALIDATED',
  refused: 'REFUSED',
  sent: 'SENT',
  'not-done': 'NOT DONE',
  na: 'N-A',
};

const FV_GRID = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1fr 150px 210px 120px' },
  gap: 1.5,
  alignItems: 'center',
  px: 1.75,
  py: 1.25,
  borderBottom: `1px solid ${colors.lightGray}`,
  fontSize: 13,
} as const;

/** Suffixe de testid stable, dérivé du libellé PAD. */
export function slug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export interface MilestoneReviewPanelProps {
  projectId: string;
  milestoneId: string;
  milestone: Milestone;
  canEdit: boolean;
  /**
   * Droit de rouvrir une revue franchie — `direction | admin` seulement, jamais
   * le manager du projet : c'est lui qui valide, lui laisser défaire son propre
   * franchissement viderait le contrôle de son sens.
   */
  canReopen: boolean;
  now: Date;
  /** Relance la lecture des jalons — sans onSnapshot, rien ne rafraîchit tout seul. */
  onDone: () => void;
}

export default function MilestoneReviewPanel({
  projectId,
  milestoneId,
  milestone,
  canEdit,
  canReopen,
  now,
  onDone,
}: MilestoneReviewPanelProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const deliverables = milestone.deliverables ?? [];
  const c = milestoneCompliance(milestone, now);
  const isDone = milestone.status === 'done';
  const inEditMode = canEdit && !isDone;

  const missing = deliverables.filter((d) => !FV_DONE_STATUSES.includes(d.status)).length;
  // La revue ne peut être validée qu'une fois toutes les fiches renseignées —
  // `not-done` est le seul état qui bloque (un `refused` est un choix assumé).
  const blocked = deliverables.some((d) => d.status === 'not-done');

  const [derogateOpen, setDerogateOpen] = useState(false);
  const [derogateComment, setDerogateComment] = useState('');
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenComment, setReopenComment] = useState('');

  const writeDeliverables = useCallback(
    async (next: FvDeliverable[]) => {
      await updateMilestoneDeliverables(projectId, milestoneId, next);
      onDone();
    },
    [projectId, milestoneId, onDone],
  );

  const handleStatus = useCallback(
    async (index: number, status: FvStatus) => {
      await writeDeliverables(
        deliverables.map((d, i) =>
          i === index
            ? { ...d, status, validatedBy: user?.uid ?? '', date: Timestamp.now() }
            : d,
        ),
      );
    },
    [deliverables, writeDeliverables, user?.uid],
  );

  const handleLink = useCallback(
    async (index: number, link: string) => {
      await writeDeliverables(
        deliverables.map((d, i) => (i === index ? { ...d, link } : d)),
      );
    },
    [deliverables, writeDeliverables],
  );

  const handleAddFv = useCallback(async () => {
    await writeDeliverables([
      ...deliverables,
      { name: t('projects.milestonesTab.fvNewName'), status: 'not-done' },
    ]);
  }, [deliverables, writeDeliverables, t]);

  return (
    <Box
      data-testid={`milestone-review-${milestoneId}`}
      sx={{ border: `1px solid ${colors.bodyText}`, borderTop: 0, backgroundColor: colors.white }}
    >
      {/* En-tête de revue */}
      <Box
        sx={{
          backgroundColor: colors.peach,
          px: 1.75,
          py: 1.4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.75,
          flexWrap: 'wrap',
          borderBottom: `1px solid ${colors.lightGray}`,
        }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{milestone.reviewName}</Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          {milestone.decision && milestone.decision !== 'pending' && (
            <Chip
              size="small"
              label={FV_STATUS_LABELS[milestone.decision as FvStatus] ?? milestone.decision}
              color={milestone.decision === 'not-pass' ? 'error' : 'success'}
              data-testid={`milestone-decision-${milestoneId}`}
            />
          )}
          {missing > 0 && (
            <Chip
              size="small"
              data-testid={`milestone-review-missing-${milestoneId}`}
              label={t('projects.milestonesTab.reviewMissing', { count: missing })}
            />
          )}
        </Stack>
      </Box>

      {/* En-tête de colonnes */}
      <Box
        sx={{
          ...FV_GRID,
          display: { xs: 'none', md: 'grid' },
          backgroundColor: colors.lightGray,
          fontSize: 11,
          fontWeight: 700,
          borderBottom: `2px solid ${colors.bodyText}`,
        }}
      >
        <Box>{t('projects.milestonesTab.fvName')}</Box>
        <Box>{t('projects.milestonesTab.fvStatus')}</Box>
        <Box>{t('projects.milestonesTab.fvLink')}</Box>
        <Box>{t('projects.milestonesTab.fvValidatedBy')}</Box>
      </Box>

      {deliverables.map((d, i) => (
        <FvRow
          key={`${d.name}-${i}`}
          deliverable={d}
          canEdit={inEditMode}
          onStatus={(status) => void handleStatus(i, status)}
          onLink={(link) => void handleLink(i, link)}
        />
      ))}

      {inEditMode && (
        <Box sx={{ px: 1.75, py: 1 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => void handleAddFv()}
            data-testid={`milestone-add-fv-${milestoneId}`}
          >
            {t('projects.milestonesTab.addFv')}
          </Button>
        </Box>
      )}

      {/* Pied de revue */}
      <Box
        sx={{
          px: 1.75,
          py: 1.4,
          display: 'flex',
          gap: 1.25,
          alignItems: 'center',
          flexWrap: 'wrap',
          borderTop: `1px solid ${colors.lightGray}`,
        }}
      >
        {inEditMode && (
          <>
            <Button
              variant="contained"
              size="small"
              disabled={blocked}
              onClick={() => void decideMilestone(projectId, milestoneId, 'pass').then(onDone)}
              data-testid={`milestone-validate-review-${milestoneId}`}
            >
              {t('projects.milestonesTab.validateReview')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDerogateOpen(true)}
              data-testid={`milestone-derogate-${milestoneId}`}
            >
              {t('projects.milestonesTab.derogate')}
            </Button>
            {/* La cible se saisit ICI aussi : un jalon à revue n'affiche jamais le
                panneau checklist, seul endroit qui portait ce champ. Sans lui, on
                ne pouvait que « replanifier » une date jamais planifiée. */}
            <TextField
              type="date"
              size="small"
              label={t('projects.milestonesTab.targetDate')}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': `milestone-target-date-${milestoneId}` } }}
              value={
                milestone.targetDate
                  ? milestone.targetDate.toDate().toISOString().split('T')[0]
                  : ''
              }
              onChange={(e) =>
                void updateMilestoneTargetDate(projectId, milestoneId, e.target.value).then(onDone)
              }
            />
            <TextField
              type="date"
              size="small"
              label={t('projects.milestonesTab.reschedule')}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': `milestone-reschedule-${milestoneId}` } }}
              value={
                milestone.rescheduledDate
                  ? milestone.rescheduledDate.toDate().toISOString().split('T')[0]
                  : ''
              }
              onChange={(e) =>
                void rescheduleMilestone(
                  projectId,
                  milestoneId,
                  e.target.value ? new Date(e.target.value) : null,
                ).then(onDone)
              }
            />
          </>
        )}

        {isDone && canReopen && (
          <Button
            variant="outlined"
            size="small"
            color="warning"
            onClick={() => setReopenOpen(true)}
            data-testid={`milestone-reopen-${milestoneId}`}
          >
            {t('projects.milestonesTab.reopen')}
          </Button>
        )}
        <Typography variant="caption" sx={{ color: colors.textMuted, ml: { md: 'auto' } }}>
          {blocked
            ? t('projects.milestonesTab.reviewBlocked', { count: missing })
            : c.daysLate > 0
            ? t('projects.milestonesTab.reviewLate', { count: c.daysLate })
            : ''}
        </Typography>
      </Box>

      {/* Dérogation — commentaire obligatoire (traçabilité qualité) */}
      <Dialog open={derogateOpen} onClose={() => setDerogateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('projects.milestonesTab.derogateTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, color: colors.textMuted }}>
            {t('projects.milestonesTab.derogateHelp')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label={t('projects.milestonesTab.derogateComment')}
            value={derogateComment}
            onChange={(e) => setDerogateComment(e.target.value)}
            slotProps={{ htmlInput: { 'data-testid': `milestone-derogate-comment` } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDerogateOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!derogateComment.trim()}
            data-testid="milestone-derogate-confirm"
            onClick={() => {
              void decideMilestone(projectId, milestoneId, 'derogated', derogateComment.trim()).then(onDone);
              setDerogateOpen(false);
              setDerogateComment('');
            }}
          >
            {t('projects.milestonesTab.derogateConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Réouverture — commentaire obligatoire, et l'ancien est CONSERVÉ : rouvrir
          doit laisser une trace, pas effacer celle de la décision annulée. */}
      <Dialog open={reopenOpen} onClose={() => setReopenOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('projects.milestonesTab.reopenTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, color: colors.textMuted }}>
            {t('projects.milestonesTab.reopenHelp')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label={t('projects.milestonesTab.reopenComment')}
            value={reopenComment}
            onChange={(e) => setReopenComment(e.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'milestone-reopen-comment' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!reopenComment.trim()}
            data-testid="milestone-reopen-confirm"
            onClick={() => {
              const trace = t('projects.milestonesTab.reopenTrace', { reason: reopenComment.trim() });
              void reopenMilestone(
                projectId,
                milestoneId,
                [milestone.comment, trace].filter(Boolean).join('\n'),
              ).then(onDone);
              setReopenOpen(false);
              setReopenComment('');
            }}
          >
            {t('projects.milestonesTab.reopenConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Une fiche de validation ────────────────────────────────────

function FvRow({
  deliverable: d,
  canEdit,
  onStatus,
  onLink,
}: {
  deliverable: FvDeliverable;
  canEdit: boolean;
  onStatus: (s: FvStatus) => void;
  onLink: (link: string) => void;
}) {
  const { t } = useTranslation();
  const id = slug(d.name);
  const [draft, setDraft] = useState<string | null>(null);
  const [linkError, setLinkError] = useState(false);

  const commitLink = () => {
    if (draft === null) return;
    const raw = draft.trim();
    // Vider le champ efface le lien ; sinon on exige une vraie URL http(s) et on
    // n'écrit RIEN si elle est invalide — la faute de frappe reste à l'écran.
    if (raw !== '' && !isHttpUrl(raw)) {
      setLinkError(true);
      return;
    }
    setLinkError(false);
    setDraft(null);
    if (raw !== (d.link ?? '')) onLink(raw);
  };

  return (
    <Box sx={FV_GRID} data-testid={`fv-row-${id}`}>
      <Typography variant="body2">{d.name}</Typography>

      {canEdit ? (
        <Select
          size="small"
          value={d.status}
          onChange={(e) => onStatus(e.target.value as FvStatus)}
          // Sur le div cliquable (role=combobox), pas sur la racine : un clic sur
          // la racine du Select MUI n'ouvre pas la liste.
          SelectDisplayProps={
            { 'data-testid': `fv-status-${id}` } as React.HTMLAttributes<HTMLDivElement>
          }
          sx={{
            fontSize: 12,
            fontWeight: 600,
            backgroundColor:
              d.status === 'not-done'
                ? '#FFF3F1'
                : FV_DONE_STATUSES.includes(d.status)
                ? colors.softGreen
                : colors.white,
          }}
        >
          {FV_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {FV_STATUS_LABELS[s]}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {FV_STATUS_LABELS[d.status]}
        </Typography>
      )}

      {canEdit ? (
        <TextField
          size="small"
          variant="standard"
          placeholder={t('projects.milestonesTab.fvLinkPlaceholder')}
          value={draft ?? d.link ?? ''}
          onChange={(e) => {
            setDraft(e.target.value);
            setLinkError(false);
          }}
          onBlur={commitLink}
          error={linkError}
          helperText={
            linkError ? (
              <span data-testid={`fv-link-error-${id}`}>
                {t('projects.milestonesTab.fvLinkInvalid')}
              </span>
            ) : undefined
          }
          slotProps={{ htmlInput: { 'data-testid': `fv-link-${id}` } }}
        />
      ) : d.link ? (
        <Link
          href={d.link}
          target="_blank"
          rel="noopener"
          variant="caption"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}
        >
          <OpenInNewIcon sx={{ fontSize: 13 }} />
          {d.link}
        </Link>
      ) : (
        <Typography variant="caption" sx={{ color: '#C9C4BC' }}>
          —
        </Typography>
      )}

      <Typography variant="caption" sx={{ color: colors.textMuted }}>
        {d.validatedBy ? `${d.validatedBy}${d.date ? ` · ${d.date.toDate().getUTCDate()}/${d.date.toDate().getUTCMonth() + 1}` : ''}` : '—'}
      </Typography>
    </Box>
  );
}
